import { supabaseClient } from "@/lib/supabase";
import dayjs from "dayjs";
import { sanitiseSearch } from "./sanitiseSearch";

/** Entities an admin filter can pick by name or by pasted UUID. */
export type AdminEntityKind = "organisation" | "form" | "input" | "diary" | "entry" | "user";

export interface AdminEntityOption {
    id: string;
    label: string;
    description?: string | null;
}

export interface AdminEntityScope {
    /** Narrow input search to one form. */
    formId?: string | null;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value: string | null | undefined): value is string {
    return Boolean(value) && UUID_RE.test((value as string).trim());
}

const SEARCH_LIMIT = 30;

function joinParts(parts: (string | null | undefined)[]): string | null {
    const joined = parts.filter((p) => p && p.trim()).join(" · ");
    return joined || null;
}

export function patientNameFromDiary(row: {
    patient_info?: unknown;
    patient_first_name?: string | null;
    patient_last_name?: string | null;
}): string {
    const info = row.patient_info as { patient_name?: string | null } | null | undefined;
    const fromParts = [row.patient_first_name, row.patient_last_name].filter(Boolean).join(" ").trim();
    return info?.patient_name?.trim() || fromParts || "Unknown patient";
}

async function searchOrganisations(q: string, byId: boolean): Promise<AdminEntityOption[]> {
    let query = supabaseClient.from("organisations").select("organisation_id, organisation_name");
    if (byId) query = query.eq("organisation_id", q);
    else if (q) query = query.ilike("organisation_name", `%${sanitiseSearch(q)}%`);
    const { data, error } = await query.order("organisation_name").limit(SEARCH_LIMIT);
    if (error) throw error;
    return (data ?? []).map((row) => ({
        id: row.organisation_id,
        label: row.organisation_name || "Unnamed organisation",
    }));
}

async function searchForms(q: string, byId: boolean): Promise<AdminEntityOption[]> {
    let query = supabaseClient.from("forms").select("form_id, form_name, locale");
    if (byId) query = query.eq("form_id", q);
    else if (q) query = query.ilike("form_name", `%${sanitiseSearch(q)}%`);
    const { data, error } = await query.order("form_name").limit(SEARCH_LIMIT);
    if (error) throw error;
    return (data ?? []).map((row) => ({
        id: row.form_id,
        label: row.form_name || "Unnamed questionnaire",
        description: row.locale,
    }));
}

async function searchInputs(q: string, byId: boolean, scope: AdminEntityScope): Promise<AdminEntityOption[]> {
    let query = supabaseClient
        .from("inputs")
        .select(
            "input_id, input_label, input_key, page:pages!inputs_page_id_fkey!inner(page_label, form_id, form:forms!public_pages_form_id_fkey(form_name))"
        );
    if (byId) query = query.eq("input_id", q);
    else if (q) query = query.ilike("input_label", `%${sanitiseSearch(q)}%`);
    if (scope.formId && !byId) query = query.eq("page.form_id", scope.formId);
    const { data, error } = await query.order("input_label").limit(SEARCH_LIMIT);
    if (error) throw error;
    return (data ?? []).map((row) => {
        const page = row.page as unknown as {
            page_label: string | null;
            form: { form_name: string | null } | null;
        } | null;
        return {
            id: row.input_id,
            label: row.input_label || row.input_key || "Unlabelled input",
            description: joinParts([page?.form?.form_name, page?.page_label]),
        };
    });
}

async function searchDiaries(q: string, byId: boolean): Promise<AdminEntityOption[]> {
    let query = supabaseClient
        .from("diaries_view")
        .select("diary_id, patient_info, patient_first_name, patient_last_name, hospital_number, organisation_name");
    if (byId) {
        query = query.eq("diary_id", q);
    } else {
        const s = sanitiseSearch(q);
        query = query.or(`patient_first_name.ilike.%${s}%,patient_last_name.ilike.%${s}%,hospital_number.ilike.%${s}%`);
    }
    const { data, error } = await query.limit(SEARCH_LIMIT);
    if (error) throw error;
    return (data ?? [])
        .filter((row) => row.diary_id)
        .map((row) => ({
            id: row.diary_id as string,
            label: patientNameFromDiary(row),
            description: joinParts([row.hospital_number, row.organisation_name]),
        }));
}

async function searchUsers(q: string, byId: boolean): Promise<AdminEntityOption[]> {
    let query = supabaseClient.from("profiles").select("user_id, name, email");
    if (byId) {
        query = query.eq("user_id", q);
    } else {
        const s = sanitiseSearch(q);
        query = query.or(`name.ilike.%${s}%,email.ilike.%${s}%`);
    }
    const { data, error } = await query.order("name").limit(SEARCH_LIMIT);
    if (error) throw error;
    return (data ?? [])
        .filter((row) => row.user_id)
        .map((row) => ({
            id: row.user_id as string,
            label: row.name || row.email || "Unnamed user",
            description: row.name ? row.email : null,
        }));
}

export interface EntryValueMatchRow {
    entry_id: string | null;
    input_label: string | null;
    form_name: string | null;
    value: string | null;
    created_at: string | null;
}

function truncate(text: string, max = 60): string {
    return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

/**
 * One card per entry from matching answer rows (newest first): the questionnaire and date,
 * then the answer that matched. With showMatch off (UUID lookup), only the date is shown.
 */
export function entryOptionsFromMatches(
    rows: EntryValueMatchRow[],
    { showMatch = true, limit = SEARCH_LIMIT }: { showMatch?: boolean; limit?: number } = {}
): AdminEntityOption[] {
    const seen = new Set<string>();
    const options: AdminEntityOption[] = [];
    for (const row of rows) {
        if (!row.entry_id || seen.has(row.entry_id)) continue;
        seen.add(row.entry_id);
        const date = row.created_at ? dayjs(row.created_at).format("DD MMM YYYY") : null;
        const match =
            showMatch && row.input_label
                ? `${row.input_label}: ${row.value ? truncate(row.value) : "no answer"}`
                : null;
        options.push({
            id: row.entry_id,
            label: row.form_name || "Untitled questionnaire",
            description: joinParts([date, match]),
        });
        if (options.length >= limit) break;
    }
    return options;
}

/** Entries have no name, so search their answers and input labels instead. */
async function searchEntries(q: string, byId: boolean): Promise<AdminEntityOption[]> {
    let query = supabaseClient.from("entry_values_view").select("entry_id, input_label, form_name, value, created_at");
    if (byId) {
        query = query.eq("entry_id", q);
    } else {
        const s = sanitiseSearch(q);
        query = query.or(`value.ilike.%${s}%,input_label.ilike.%${s}%`);
    }
    // Several answers can match per entry, so fetch extra rows and keep one card per entry.
    const { data, error } = await query
        .order("created_at", { ascending: false, nullsFirst: false })
        .limit(byId ? 1 : SEARCH_LIMIT * 8);
    if (error) throw error;
    return entryOptionsFromMatches((data ?? []) as EntryValueMatchRow[], { showMatch: !byId });
}

/**
 * Whether an empty search box should list results straight away. Organisations and
 * questionnaires are small lists; inputs only when scoped to one form.
 */
export function listsWithoutQuery(entity: AdminEntityKind, scope: AdminEntityScope = {}): boolean {
    if (entity === "organisation" || entity === "form") return true;
    if (entity === "input") return Boolean(scope.formId);
    return false;
}

export async function searchAdminEntity(
    entity: AdminEntityKind,
    rawQuery: string,
    scope: AdminEntityScope = {}
): Promise<AdminEntityOption[]> {
    const q = rawQuery.trim();
    const byId = isUuid(q);
    if (!byId && !q && !listsWithoutQuery(entity, scope)) return [];
    switch (entity) {
        case "organisation":
            return searchOrganisations(q, byId);
        case "form":
            return searchForms(q, byId);
        case "input":
            return searchInputs(q, byId, scope);
        case "diary":
            return searchDiaries(q, byId);
        case "user":
            return searchUsers(q, byId);
        case "entry":
            return searchEntries(q, byId);
    }
}

/** Look up the display label for a selected id. Null when the id is unknown. */
export async function resolveAdminEntity(entity: AdminEntityKind, id: string): Promise<AdminEntityOption | null> {
    if (!isUuid(id)) return null;
    const [match] = await searchAdminEntity(entity, id);
    return match ?? null;
}
